import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import { cloneDeep } from "lodash-es"
import * as API from "services/api"
import { errorToast, successToast } from "shared/toast"
import * as ReadableTypes from "utils/readableTypes"

const SLICE_NAME = "formSubmissionConversations"

export const PUBLIC_CONTEXT = "public-context"
const ACTIVITY_HUB_CONTEXT = "activity-hub-context"

const UNINITIALIZED = "uninitialized"
const INITIALIZING = "initializing"
const REFRESHING = "refreshing"
const LOADED = "loaded"

const INITIAL_STATE = {
  context: null,
  conversations: [],
  formSubmissionSlug: undefined,
  status: UNINITIALIZED,
}

let setContext
let setFormSubmissionSlug

export const initialize = createAsyncThunk(
  `${SLICE_NAME}/initialize`,
  async ({ formSubmissionSlug, context = ACTIVITY_HUB_CONTEXT }, thunkAPI) => {
    const { dispatch, getState } = thunkAPI

    await dispatch(setContext({ context }))
    await dispatch(setFormSubmissionSlug({ formSubmissionSlug }))

    const apiFunction = getState()[SLICE_NAME].context === PUBLIC_CONTEXT
      ? API.publicFormSubmissionConversations
      : API.formSubmissionConversations

    const response = await apiFunction({ formSubmissionSlug })

    let conversations = []

    if (response.ok) {
      conversations = response.data
    } else {
      throw new Error("Something went wrong. Unable to load conversations.")
    }

    return (
      { conversations }
    )
  },
)

export const closeConversation = createAsyncThunk(
  `${SLICE_NAME}/closeConversation`,
  async ({ conversationId }, thunkAPI) => {
    const { formSubmissionSlug } = thunkAPI.getState()[SLICE_NAME]

    const response = await API.closeFormSubmissionConversation({ conversationId, formSubmissionSlug })

    if (!response.ok) {
      throw new Error("Something went wrong. Unable to close conversation.")
    }

    return (
      { conversationId }
    )
  },
)

export const reopenConversation = createAsyncThunk(
  `${SLICE_NAME}/reopenConversation`,
  async ({ conversationId }, thunkAPI) => {
    const { formSubmissionSlug } = thunkAPI.getState()[SLICE_NAME]

    const response = await API.reopenFormSubmissionConversation({ conversationId, formSubmissionSlug })

    if (!response.ok) {
      throw new Error("Something went wrong. Unable to reopen conversation.")
    }

    return (
      { conversationId }
    )
  },
)

export const openConversation = createAsyncThunk(
  `${SLICE_NAME}/openConversation`,
  async ({ conversationData }, thunkAPI) => {
    const { formSubmissionSlug } = thunkAPI.getState()[SLICE_NAME]

    const response = await API.createFormSubmissionConversation({ ...conversationData, formSubmissionSlug })

    if (!response.ok) {
      throw new Error("Something went wrong. Unable to open conversation.")
    }

    return (
      { conversation: response.data }
    )
  },
)

export const refresh = createAsyncThunk(
  `${SLICE_NAME}/refresh`,
  async (_, thunkAPI) => {
    const { getState } = thunkAPI
    const { context, formSubmissionSlug } = getState()[SLICE_NAME]

    const apiFunction = context === PUBLIC_CONTEXT
      ? API.publicFormSubmissionConversations
      : API.formSubmissionConversations

    const response = await apiFunction({ formSubmissionSlug })

    if (!response.ok) {
      throw new Error("Something went wrong. Unable to load conversations.")
    }

    return { conversations: response.data }
  },
)

export const addMessageToConversation = createAsyncThunk(
  `${SLICE_NAME}/addMessageToConversation`,
  async ({ conversationId, messageData }, thunkAPI) => {
    const { getState } = thunkAPI
    const { formSubmissionSlug } = getState()[SLICE_NAME]
    const { text } = messageData

    const apiFunction = getState()[SLICE_NAME].context === PUBLIC_CONTEXT
      ? API.publicCreateConversationMessage
      : API.createConversationMessage

    const response = await apiFunction({ conversationId, formSubmissionSlug, text })

    if (!response.ok) {
      throw new Error("Something went wrong.  Unable to add your message to the conversation.")
    }

    return (
      { conversationId, message: response.data }
    )
  },
)

export const markConversationMessagesAsRead = createAsyncThunk(
  `${SLICE_NAME}/markConversationMessagesAsRead`,
  async ({ conversationId }, thunkAPI) => {
    const { conversations } = thunkAPI.getState()[SLICE_NAME]
    const conversation = conversations.find((c) => c.id === conversationId)

    if (conversation) {
      const unreadMessages = conversation.messages.filter((m) => !m.read)

      await Promise.all(
        unreadMessages.map((message) => API.createReadReceipt({
          readableType: ReadableTypes.MESSAGE,
          readableId: message.id,
        })),
      )
    }

    return { conversationId }
  },
)

export const formSubmissionConversationsSlice = createSlice({
  name: SLICE_NAME,
  initialState: cloneDeep(INITIAL_STATE),
  reducers: {
    addMessageToConversation: (state, action) => {
      const { conversationId, message } = action.payload
      const conversation = state.conversations.find((c) => c.id === conversationId)

      if (!conversation) return

      const { messages } = conversation
      const updatedMessages = [message, ...messages]

      conversation.messages = updatedMessages
    },
    setContext: (state, action) => {
      const { context } = action.payload

      state.context = context
    },
    resetData: (state) => {
      Object.assign(state, cloneDeep(INITIAL_STATE))
    },
    setFormSubmissionSlug: (state, action) => {
      const { formSubmissionSlug } = action.payload

      state.formSubmissionSlug = formSubmissionSlug
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(initialize.pending, (state) => {
        state.status = INITIALIZING
      })
      .addCase(initialize.fulfilled, (state, action) => {
        const { conversations } = action.payload

        state.conversations = conversations
        state.status = LOADED
      })
      .addCase(initialize.rejected, (state, action) => {
        state.status = LOADED
        errorToast(action.error.message)
      })
      .addCase(refresh.pending, (state) => {
        state.status = REFRESHING
      })
      .addCase(refresh.fulfilled, (state, action) => {
        const { conversations } = action.payload

        state.conversations = conversations
        state.status = LOADED
      })
      .addCase(refresh.rejected, (state, action) => {
        state.status = LOADED
        errorToast(action.error.message)
      })
      .addCase(closeConversation.fulfilled, (state, action) => {
        const { conversationId } = action.payload

        const conversation = state.conversations.find((c) => c.id === conversationId)

        if (!conversation) return

        conversation.closed = true
        successToast("Conversation closed successfully.")
      })
      .addCase(closeConversation.rejected, (_state, action) => {
        errorToast(action.error.message)
      })
      .addCase(reopenConversation.fulfilled, (state, action) => {
        const { conversationId } = action.payload

        const conversation = state.conversations.find((c) => c.id === conversationId)

        if (!conversation) return

        conversation.closed = false
        successToast("Conversation reopened successfully.")
      })
      .addCase(reopenConversation.rejected, (_state, action) => {
        errorToast(action.error.message)
      })
      .addCase(openConversation.fulfilled, (state, action) => {
        const { conversation } = action.payload

        state.conversations.push(conversation)
        successToast("New conversation opened successfully.")
      })
      .addCase(openConversation.rejected, (_state, action) => {
        errorToast(action.error.message)
      })
      .addCase(addMessageToConversation.fulfilled, (state, action) => {
        const { conversationId, message } = action.payload
        const conversation = state.conversations.find((c) => c.id === conversationId)

        if (!conversation) return

        const { messages } = conversation
        const updatedMessages = [message, ...messages]

        conversation.messages = updatedMessages
        successToast("Your message was added to the conversation successfully.")
      })
      .addCase(addMessageToConversation.rejected, (_state, action) => {
        errorToast(action.error.message)
      })
      .addCase(markConversationMessagesAsRead.fulfilled, (state, action) => {
        const { conversationId } = action.payload
        const conversation = state.conversations.find((c) => c.id === conversationId)

        if (!conversation) return

        conversation.messages.forEach((message) => {
          message.read = true
        })
      })
  },
})

export const { resetData: resetConversationsData } = formSubmissionConversationsSlice.actions

setContext = formSubmissionConversationsSlice.actions.setContext
setFormSubmissionSlug = formSubmissionConversationsSlice.actions.setFormSubmissionSlug

export const context = (state) => state[SLICE_NAME].context

export const conversations = (state) => state[SLICE_NAME].conversations

export const hasConversations = (state) => conversations(state).length > 0

export const allConversationsClosed = (state) => conversations(state).every((c) => c.closed)

export const conversationIsRead = (conversationId) => (state) => {
  const conversation = conversations(state).find((c) => c.id === conversationId)

  if (!conversation) return true

  return conversation.messages.every((m) => m.read)
}

export const initializing = (state) => {
  const { status } = state[SLICE_NAME]
  return status === UNINITIALIZED || status === INITIALIZING
}

export const loading = (state) => {
  const { status } = state[SLICE_NAME]
  return status === INITIALIZING || status === REFRESHING
}

export const titles = (state) => state[SLICE_NAME].conversations.map((conversation) => conversation.title)

export default formSubmissionConversationsSlice.reducer
